import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  Inject,
  OnInit,
  ViewEncapsulation,
} from '@angular/core';
import { UntypedFormBuilder, UntypedFormGroup } from '@angular/forms';
import {
  MAT_LEGACY_DIALOG_DATA as MAT_DIALOG_DATA,
  MatLegacyDialogRef as MatDialogRef,
} from '@angular/material/legacy-dialog';
import { OnDestroyMixin } from '@w11k/ngx-componentdestroyed';
import { AccountService } from 'app/core';
import { Account } from 'app/core/models/account.types';

// List of keywords and patterns to disallow
const BLACKLIST_KEYWORDS = [
  'alert',
  'document',
  'window',
  'eval',
  'function',
  'console',
  'prompt',
  'confirm',
  'setTimeout',
  'setInterval',
  'XMLHttpRequest',
  'fetch',
  'location',
  'history',
  'innerHTML',
  'outerHTML',
  'appendChild',
  'write',
  'writeln',
  'cookie',
  'localStorage',
  'sessionStorage',
  'execCommand',
  'onerror',
  'onload',
  'onsubmit',
  'onkeydown',
  'onkeyup',
  'onkeypress',
  'onmouseover',
  'onmouseout',
  'onmousemove',
  'onmousedown',
  'onmouseup',
  'onmouseenter',
  'onmouseleave',
  'onchange',
  'oninput',
  'onfocus',
  'onblur',
  'ondblclick',
  'oncontextmenu',
  'onwheel',
  'onselect',
  'oncopy',
  'oncut',
  'onpaste',
  'onabort',
  'onresize',
  'onhashchange',
  'onmessage',
  'onpopstate',
  'onstorage',
  'onunload',
  'ontoggle',
  'onanimationstart',
  'onanimationiteration',
  'onanimationend',
  'ontransitionstart',
  'ontransitionend',
  'ontransitioncancel',
  'onpointerdown',
  'onpointerup',
  'onpointercancel',
  'onpointermove',
  'onpointerover',
  'onpointerout',
  'onpointerenter',
  'onpointerleave',
  'onwheel',
  'onscroll',
  '() =>',
];

const BLACKLIST_PATTERNS = [
  /\b(?:document\.(?:write|writeln)|window\.(?:alert|confirm|prompt))\b/gi,
  /\bon\w+\s*=\s*['"][^'"]*['"]/gi, // Matches any on<Event> attributes in HTML
  /=>/g, // Matches any arrow function
  // Add more patterns as needed
];

@Component({
  selector: 'panel-conditional-logic-modal',
  templateUrl: './panel-conditional-logic-modal.component.html',
  styleUrls: ['./panel-conditional-logic-modal.component.scss'],
  encapsulation: ViewEncapsulation.None,
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class PanelConditionalLogicModal extends OnDestroyMixin implements OnInit {
  private account: Account;
  private conditionalLogic: any;
  public panelLogicForm: UntypedFormGroup;
  public error: string;

  constructor(
    @Inject(MAT_DIALOG_DATA) private readonly data: any,
    private readonly matDialogRef: MatDialogRef<PanelConditionalLogicModal>,
    private readonly formBuilder: UntypedFormBuilder,
    private readonly accountService: AccountService,
    private readonly changeDetectorRef: ChangeDetectorRef,
  ) {
    super();
    this.account = this.accountService.getCurrentAccount();
  }

  ngOnInit(): void {
    this.conditionalLogic = this.data.conditional_logic;

    this.panelLogicForm = this.formBuilder.group({
      conditional_logic: [''],
    });
    this.panelLogicForm.get('conditional_logic').setValue(this.conditionalLogic);
  }

  discard(): void {
    this.matDialogRef.close(undefined);
  }

  save(): void {
    const logicValue = this.panelLogicForm.get('conditional_logic').value;

    try {
      this.validateConditionalLogic(logicValue);
    } catch (error) {
      this.error = error.message;
      this.changeDetectorRef.detectChanges();
      return;
    }

    this.matDialogRef.close({
      conditional_logic: logicValue,
    });
  }

  validateConditionalLogic(input: string): boolean {
    if (!input || input.trim().length === 0) {
      return true;
    }
    // Check for blacklisted keywords
    for (const keyword of BLACKLIST_KEYWORDS) {
      if (input.toLocaleLowerCase().includes(keyword)) {
        throw new Error(`Keyword "${keyword}" is not allowed.`);
      }
    }

    // Check for blacklisted patterns
    for (const pattern of BLACKLIST_PATTERNS) {
      if (pattern.test(input)) {
        throw new Error(`Pattern "${pattern}" is not allowed.`);
      }
    }

    return true;
  }
}
